// FILE: src/services/examLogisticsService.js
//
// Exam Logistics checklist for the Study Plan Command Center.
//
// Storage: learner_prep_tasks (migration 025, widened in 069) — row present
// = complete, un-checking deletes the row. The three original PREP_TASKS keys
// (training_hours / application / schedule_exam) are REUSED here so state
// stays shared with the Milestones-page ExamPrepChecklist; the additional
// keys below are new and only surfaced on the Study Plan page.
//
// Everything here is self-attested — nothing pretends to be automated.

import { supabase } from './supabaseClient';
import { getExamConfig, getExamBreaks } from '../config/exams';

// Required training hours per cert (PMOCP's 10 = the PMOGA prep course).
const TRAINING_HOURS = { pmp: 35, cpmai: 25, 'pmi-pmocp': 10 };

// dueOffsetDays = recommended completion this many days BEFORE the exam date.
// certs: 'all' or an array of examIds. `when` tags purely-conditional items
// (rendered with an "if remote" style tag, never counted as overdue).
export const LOGISTICS_TASKS = [
  { key: 'training_hours', certs: ['pmp', 'cpmai', 'pmi-pmocp'], dueOffsetDays: null,
    title: 'Complete required training hours',
    sub: (examId) => `${TRAINING_HOURS[examId] || 35} training hours` },
  { key: 'application', certs: ['pmp'], dueOffsetDays: 21,
    title: 'Submit your PMP application',
    sub: () => 'allow time for PMI review' },
  { key: 'exam_paid', certs: 'all', dueOffsetDays: 14,
    title: 'Pay the exam fee',
    sub: () => 'after PMI confirms eligibility' },
  { key: 'schedule_exam', certs: 'all', dueOffsetDays: 3,
    title: 'Schedule your exam',
    sub: () => 'book a test center or online proctor slot' },
  { key: 'location_choice', certs: 'all', dueOffsetDays: 7,
    title: 'Choose remote or test center',
    sub: () => null },
  { key: 'id_confirmed', certs: 'all', dueOffsetDays: 7,
    title: 'Confirm your ID matches PMI records',
    sub: () => null },
  { key: 'system_test', certs: 'all', dueOffsetDays: 3, when: 'If remote',
    title: 'Run the online-proctor system test',
    sub: () => 'remote exams only' },
  { key: 'workspace_prep', certs: 'all', dueOffsetDays: 1, when: 'If remote',
    title: 'Prepare your exam workspace',
    sub: () => 'clear desk, quiet room — remote exams only' },
  { key: 'break_strategy', certs: 'breaks', dueOffsetDays: 3,
    title: 'Review the break strategy',
    sub: (examId) => {
      const b = getExamBreaks(examId) || [];
      if (!b.length) return null;
      return `${b[0].durationMinutes}-min break${b.length > 1 ? 's' : ''} after Q${b.map((x) => x.afterQuestion).join(' / Q')}`;
    } },
];

function appliesTo(task, examId) {
  if (task.certs === 'all') return true;
  if (task.certs === 'breaks') return (getExamBreaks(examId) || []).length > 0;
  return Array.isArray(task.certs) && task.certs.includes(examId);
}

/** Task definitions applicable to a cert (config-driven, all 4 certs). */
export function logisticsTasksForExam(examId) {
  if (!examId || !getExamConfig(examId)) return [];
  return LOGISTICS_TASKS.filter((t) => appliesTo(t, examId));
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

function dueInfo(task, targetExamDate, completed) {
  if (task.dueOffsetDays == null || !targetExamDate) return { dueDate: null, overdue: false };
  try {
    const due = new Date(targetExamDate);
    due.setUTCDate(due.getUTCDate() - task.dueOffsetDays);
    const dueDate = due.toISOString().slice(0, 10);
    // Conditional ("If remote") items are never flagged overdue.
    const overdue = !task.when && !completed && todayIso() > dueDate;
    return { dueDate, overdue };
  } catch (_) { return { dueDate: null, overdue: false }; }
}

/**
 * Load the logistics checklist for a cert merged with completion state.
 * Signed-out learners get the catalog with completed=false (read-only UI).
 */
export async function loadLogistics(examId, { targetExamDate = null } = {}) {
  const defs = logisticsTasksForExam(examId);
  let doneKeys = new Set();
  try {
    const { data, error } = await supabase
      .from('learner_prep_tasks')
      .select('task_key')
      .eq('exam_id', examId);
    if (!error && Array.isArray(data)) doneKeys = new Set(data.map((r) => r.task_key));
  } catch (_) { /* signed-out / offline — render unchecked */ }

  return defs.map((t) => {
    const completed = doneKeys.has(t.key);
    return {
      key: t.key,
      title: t.title,
      sub: t.sub ? t.sub(examId) : null,
      when: t.when || null,
      completed,
      ...dueInfo(t, targetExamDate, completed),
    };
  });
}

/** Check / un-check a logistics (or reading) key. Returns success boolean. */
export async function setLogisticsDone(taskKey, examId, done) {
  if (!taskKey || !examId) return false;
  try {
    if (done) {
      const { error } = await supabase
        .from('learner_prep_tasks')
        .insert({ task_key: taskKey, exam_id: examId });
      // A unique-violation just means it was already complete — not an error.
      if (error && !/duplicate|unique|23505/i.test(error.message || error.code || '')) return false;
      return true;
    }
    const { error } = await supabase
      .from('learner_prep_tasks')
      .delete()
      .eq('task_key', taskKey)
      .eq('exam_id', examId);
    return !error;
  } catch (_) { return false; }
}
